import { fetchJSON as fetchAPI } from './client';

export type StructuredLog = {
  id: string;
  tenant_id: string;
  level: string;
  service: string;
  message: string;
  correlation_id?: string;
  trace_id?: string;
  request_id?: string;
  pii_redacted: boolean;
  sampled: boolean;
  recorded_at: string;
};

export type MetricRecord = {
  id: string;
  tenant_id: string;
  name: string;
  kind: string;
  service: string;
  unit?: string;
  value: number;
  p95_ms?: number;
  recorded_at: string;
};

export type TraceRecord = {
  id: string;
  tenant_id: string;
  trace_id: string;
  correlation_id?: string;
  kind: string;
  service: string;
  operation: string;
  duration_ms: number;
  status: string;
  span_count: number;
  started_at: string;
};

export type ModuleHealth = {
  id: string;
  tenant_id: string;
  module: string;
  display_name: string;
  status: string;
  latency_ms: number;
  error_rate_pct: number;
  availability_pct: number;
  checked_at: string;
};

export type AlertRecord = {
  id: string;
  tenant_id: string;
  name: string;
  severity: string;
  service: string;
  status: string;
  message: string;
  escalation_policy?: string;
  raised_at: string;
  resolved_at?: string;
};

export type SLORecord = {
  id: string;
  tenant_id: string;
  service: string;
  name: string;
  target_pct: number;
  current_pct: number;
  error_budget_pct: number;
  latency_target_ms: number;
  compliant: boolean;
  evaluated_at: string;
};

export type CapacityRecord = {
  id: string;
  tenant_id: string;
  resource: string;
  module: string;
  used_pct: number;
  forecast_pct_30d: number;
  threshold_pct: number;
  status: string;
  recorded_at: string;
};

export type ObservabilityHealth = {
  tenant_id: string;
  platform_health_score: number;
  availability_pct: number;
  mttd_mins: number;
  mttr_mins: number;
  error_rate_pct: number;
  slo_compliance_pct: number;
  capacity_utilization_pct: number;
  active_alerts: number;
  open_incidents: number;
  healthy_modules: number;
  validated_at: string;
};

export type ExtensionPoint = {
  dev_id: string;
  name: string;
  description: string;
};

export type ObservabilityGovernanceDashboard = {
  tenant_id: string;
  health: ObservabilityHealth;
  metrics: number;
  logs: number;
  traces: number;
  alerts: number;
  slos: number;
  capacity_records: number;
  module_health: number;
  extension_points: ExtensionPoint[];
  generated_at: string;
};

const OBS_BASE = import.meta.env.VITE_OBSERVABILITY_API ?? '/api/v1/observability';

function fetchObs<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${OBS_BASE}${path}`, init);
}

export const observabilityGovernanceApi = {
  dashboard: () => fetchObs<ObservabilityGovernanceDashboard>('/governance/dashboard'),
  health: () => fetchObs<ObservabilityHealth>('/governance/health'),
  listLogs: () => fetchObs<{ logs: StructuredLog[] }>('/governance/logs').then((r) => r.logs),
  listMetrics: () => fetchObs<{ metrics: MetricRecord[] }>('/governance/metrics').then((r) => r.metrics),
  listTraces: () => fetchObs<{ traces: TraceRecord[] }>('/governance/traces').then((r) => r.traces),
  listModules: () => fetchObs<{ modules: ModuleHealth[] }>('/governance/modules').then((r) => r.modules),
  listAlerts: () => fetchObs<{ alerts: AlertRecord[] }>('/governance/alerts').then((r) => r.alerts),
  listSLOs: () => fetchObs<{ slos: SLORecord[] }>('/slos').then((r) => r.slos),
  listCapacity: () => fetchObs<{ capacity: CapacityRecord[] }>('/governance/capacity').then((r) => r.capacity),
  resolveAlert: (id: string) =>
    fetchObs<{ resolved: boolean; alert_id: string }>(`/governance/alerts/${id}/resolve`, { method: 'POST' }),
  extensionPoints: () =>
    fetchObs<{ extension_points: ExtensionPoint[] }>('/extensions').then((r) => r.extension_points),
};
